<script lang="ts">
  import type { Colonist } from '@sim/types';
  import { moodBand } from '@sim/selectors';

  export let c: Colonist;
  export let selected = false;
  export let seated = false; // sitting at a station (stool/couch) — legs tuck away
  export let onSelect: (id: number) => void;

  $: moving = c.activity === 'goto';
  $: working = c.activity === 'work';
  $: sleeping = c.activity === 'sleep' || c.activity === 'recover';
  $: mood = moodBand(c.mood).color;
</script>

<button
  class="pawn"
  class:selected
  class:moving
  class:working
  class:sleeping
  class:seated
  on:click|stopPropagation={() => onSelect(c.id)}
  aria-label={c.name}
  title={c.name}
>
  <svg viewBox="0 0 30 46" width="26" height="40">
    {#if selected}
      <ellipse cx="15" cy="43" rx="11" ry="3" fill="none" stroke="#fff" stroke-width="1.4" opacity="0.9" />
    {/if}

    {#if sleeping}
      <!-- dedicated lying form: head on the pillow (left), body under a blanket -->
      <ellipse class="shadow" cx="15" cy="44.5" rx="12" ry="2" fill="#000" opacity="0.25" />
      <g class="lyingbody">
        <rect x="7" y="35" width="20.5" height="8.5" rx="4" fill={c.accent} stroke="#0006" stroke-width="0.5" />
        <rect x="7" y="38.4" width="20.5" height="1.8" fill="#0003" />
      </g>
      <circle cx="6.4" cy="37.5" r="4.6" fill={c.skin} />
      <!-- hair on the back of the head (pillow side) + a closed eye -->
      <path d="M6.9 32.95 A4.65 4.65 0 0 0 6.9 42.05 Z" fill="#2a2330" opacity="0.85" />
      <path d="M8.4 37.3 q1.1 1 2.3 0" stroke="#2a2330" stroke-width="0.7" fill="none" opacity="0.8" />
      <text x="12" y="30" class="zzz">z</text>
    {:else}
      <ellipse class="shadow" cx="15" cy="43" rx="8" ry="2.2" fill="#000" opacity="0.3" />
      <g class="figure">
        <!-- legs -->
        <g class="legs">
          <rect class="legB" x="11" y="32" width="3.4" height="9" rx="1.6" fill="#26303f" />
          <rect class="legF" x="15.6" y="32" width="3.4" height="9" rx="1.6" fill="#26303f" />
        </g>
        <!-- torso -->
        <g class="body">
          <rect class="armB" x="8.4" y="22" width="3" height="10" rx="1.5" fill={c.accent} opacity="0.8" />
          <rect x="9.5" y="20" width="11" height="14" rx="4" fill={c.accent} stroke="#0006" stroke-width="0.5" />
          <rect x="9.5" y="29" width="11" height="2" fill="#0003" />
          <g class="armF"><rect x="18.6" y="22" width="3" height="10" rx="1.5" fill={c.accent} /></g>
          <!-- head -->
          <circle cx="15" cy="15" r="5" fill={c.skin} />
          <path d="M10 14 Q15 8 20 14 Z" fill="#2a2330" opacity="0.85" />
        </g>
      </g>
      <!-- mood pip -->
      <circle cx="15" cy="6" r="2.4" fill={mood} stroke="#0008" stroke-width="0.5" />
      {#if working}<circle cx="22" cy="24" r="1.6" fill="#ffd54a" class="spark" />{/if}
    {/if}
  </svg>
</button>

<style>
  .pawn {
    display: inline-block;
    padding: 0;
    background: none;
    border: none;
    line-height: 0;
    cursor: pointer;
  }
  .lyingbody {
    transform-box: fill-box;
    transform-origin: left center;
    animation: breathe-lying 2.6s ease-in-out infinite;
  }
  @keyframes breathe-lying {
    0%, 100% { transform: scaleY(1); }
    50% { transform: scaleY(0.92) translateY(0.4px); }
  }
  .seated .legs {
    opacity: 0;
  }
  .seated .figure {
    transform: translateY(8px);
  }
  .body {
    transform-box: fill-box;
    transform-origin: bottom center;
  }
  .armF,
  .legB,
  .legF {
    transform-box: fill-box;
    transform-origin: top center;
  }
  .moving .legF {
    animation: step 0.45s ease-in-out infinite;
  }
  .moving .legB {
    animation: step 0.45s ease-in-out infinite reverse;
  }
  .working .armF {
    animation: pump 0.7s ease-in-out infinite;
  }
  .working .body {
    animation: bob 0.7s ease-in-out infinite;
  }
  .zzz {
    font-size: 7px;
    fill: #cfe;
    animation: float 2s ease-in-out infinite;
  }
  .spark {
    animation: pulse 0.7s ease-in-out infinite;
  }
  @keyframes step {
    0%, 100% { transform: rotate(15deg); }
    50% { transform: rotate(-15deg); }
  }
  @keyframes pump {
    0%, 100% { transform: rotate(6deg); }
    50% { transform: rotate(-38deg); }
  }
  @keyframes bob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-1.2px); }
  }
  @keyframes float {
    0% { opacity: 0; transform: translateY(0); }
    50% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-4px); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }
</style>
